import React from 'react';
import { EmailIcon } from './emailProviderIcons';
import styles from './EmailConnectorTab.module.css';

export type Tone = 'ok' | 'warn' | 'bad' | 'off';

const pillClass: Record<Tone, string> = {
  ok: styles.pillOk,
  warn: styles.pillWarn,
  bad: styles.pillBad,
  off: styles.pillOff,
};

export const StatusPill: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`${styles.pill} ${pillClass[tone]}`}>{children}</span>
);

interface FieldProps {
  label: string;
  hint?: string;
  wide?: boolean;
  children: React.ReactElement<{ id?: string; 'aria-describedby'?: string }>;
}

/** Label + control + optional hint, laid out like the other connector tabs' fields. */
export const Field: React.FC<FieldProps> = ({ label, hint, wide, children }) => {
  const id = React.useId();
  const hintId = React.useId();
  return (
    <div className={[styles.field, wide ? styles.fieldWide : ''].filter(Boolean).join(' ')}>
      <label htmlFor={id}>{label}</label>
      {React.cloneElement(children, { id, 'aria-describedby': hint ? hintId : undefined })}
      {hint ? <span id={hintId} className="company-field-hint">{hint}</span> : null}
    </div>
  );
};

export interface ChipOption<T extends string> {
  id: T;
  label: string;
  icon: string;
}

interface ChipGroupProps<T extends string> {
  value: T;
  options: ChipOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  ariaLabel: string;
  testId?: string;
}

export const ChipGroup = <T extends string>({ value, options, onChange, disabled, ariaLabel, testId }: ChipGroupProps<T>) => (
  <div className={styles.chips} role="radiogroup" aria-label={ariaLabel} data-testid={testId}>
    {options.map(option => {
      const selected = option.id === value;
      return (
        <button
          key={option.id}
          type="button"
          role="radio"
          aria-checked={selected}
          className={`${styles.chip} ${selected ? styles.chipOn : ''}`}
          onClick={() => onChange(option.id)}
          disabled={disabled}
        >
          <EmailIcon id={option.icon} className={styles.chipIcon} />
          {option.label}
        </button>
      );
    })}
  </div>
);

export const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 8l5 5 5-5" />
  </svg>
);
